<template>
  <v-row class="mt-4">
    <v-col cols="12">
      <div class="prompt-title-row">
        <h2 class="prompt-title">
          Generate a tech-infused lesson package for the topic you're teaching
        </h2>
        <v-btn
          v-if="isMobile"
          @click="toggleDropdown"
          class="toggle-btn mobile-toggle-btn"
          :class="{ active: showDropdown, 'has-filter': hasAnyFilter }"
          :style="{ '--button-color': colorButton }"
          aria-label="Toggle Advanced Options"
        >
          <v-icon>{{ showDropdown ? "mdi-close" : "mdi-tune-variant" }}</v-icon>
        </v-btn>
      </div>
    </v-col>
  </v-row>
  <v-row class="form-row">
    <v-col cols="12" md="8" class="topic-col">
      <BaseInput
        type="multiline"
        v-model="topic"
        :error="isTopicError"
        :errorMessage="topicErrorMessage"
        placeholder="Topic or a longer description of what you’re teaching (e.g. decimals, narrative writing, organic chemistry...)"
        :maxHeight="'300'"
        :rows="7"
        class="topic-input"
        name="topic"
        resize="none"
      />
    </v-col>
    <v-col cols="12" md="4">
      <div v-if="!isMobile" class="filter-btn-wrapper">
        <v-menu
          v-model="showDropdown"
          :close-on-content-click="false"
          :persistent="true"
          :no-click-animation="true"
          :scrim="false"
          location="bottom end"
          origin="top end"
          offset="8"
          scroll-strategy="none"
        >
          <template #activator="{ props: activatorProps }">
            <v-btn
              v-bind="activatorProps"
              class="toggle-btn"
              :class="{ active: showDropdown, 'has-filter': hasAnyFilter }"
              :style="{ '--button-color': colorButton }"
              aria-label="Toggle Advanced Options"
            >
              <v-icon>mdi-tune-variant</v-icon>
            </v-btn>
          </template>

          <v-card class="advanced-menu-card" elevation="3">
            <div class="advanced-menu-header">
              <span class="advanced-menu-title">Advanced Options</span>
              <v-icon
                role="button"
                tabindex="0"
                aria-label="Close Advanced Options"
                class="advanced-menu-close"
                @click="closeDesktopMenu"
                @keydown.enter="closeDesktopMenu"
                @keydown.space.prevent="closeDesktopMenu"
                >mdi-close</v-icon
              >
            </div>
            <div class="advanced-sheet-body">
              <div class="advanced-sheet-field">
                <label class="advanced-sheet-label">ESTREAM Mindset</label>
                <SingleSelect
                  v-model="mindsets"
                  :options="mindsetOptions"
                  placeholder="Select Mindset"
                  :clearable="true"
                  :teleport="true"
                  :match-trigger-width="true"
                  order="a-z"
                />
              </div>
              <div class="advanced-sheet-field">
                <label class="advanced-sheet-label">ESTREAM Skillset</label>
                <SingleSelect
                  v-model="skillSets"
                  :options="skillsetOptions"
                  placeholder="Select Skillset"
                  :clearable="true"
                  :teleport="true"
                  :match-trigger-width="true"
                  order="a-z"
                />
              </div>
              <div class="advanced-sheet-field">
                <label class="advanced-sheet-label">Software</label>
                <SingleSelect
                  v-model="software"
                  :options="softwareOptions"
                  placeholder="Select Software"
                  :clearable="true"
                  :teleport="true"
                  :match-trigger-width="true"
                  order="a-z"
                />
              </div>
              <div class="advanced-sheet-field">
                <label class="advanced-sheet-label">Student Profile</label>
                <MultipleSelect
                  v-model="studentProfile"
                  :options="profileOptions"
                  placeholder="Select Student Profile"
                  :match-trigger-width="true"
                  :teleport="true"
                  order="a-z"
                />
              </div>
            </div>

            <v-divider class="advanced-sheet-divider"></v-divider>

            <div class="advanced-sheet-actions">
              <ActionButton
                variant="secondary"
                full-width
                @click="clearAdvancedFilters"
              >
                Clear Filter
              </ActionButton>
              <ActionButton
                variant="primary"
                full-width
                :disabled="!hasAnyFilter"
                @click="closeDesktopMenu"
              >
                Apply Filter
              </ActionButton>
            </div>
          </v-card>
        </v-menu>
      </div>
      <v-row>
        <v-col cols="12">
          <div class="advanced-sheet-field">
            <label class="advanced-sheet-label">Grade*</label>
            <SingleSelect
              v-model="grade"
              :options="gradeOptions"
              placeholder="Choose Grade"
              :match-trigger-width="true"
              :teleport="true"
              order="a-z"
            />
            <span v-if="isGradeError" class="field-error">
              {{ gradeErrorMessage }}
            </span>
          </div>
        </v-col>
        <v-col cols="12">
          <div class="advanced-sheet-field">
            <label class="advanced-sheet-label">Subject*</label>
            <SingleSelect
              v-model="subject"
              :options="subjectOptions"
              placeholder="Choose Subject"
              :match-trigger-width="true"
              :teleport="true"
              order="a-z"
            />
            <span v-if="isSubjectError" class="field-error">
              {{ subjectErrorMessage }}
            </span>
          </div>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
  <v-row>
    <v-col cols="12" class="text-right">
      <ActionButton
        variant="primary"
        class="btn-main btn-generate generate-button"
        :disabled="props.disableButton"
        @click="emitGenerateClicked"
      >
        Generate
      </ActionButton>
    </v-col>
  </v-row>

  <v-bottom-sheet v-model="mobileSheet" :scrim="true">
    <div class="advanced-sheet">
      <div class="advanced-sheet-header">
        <span class="advanced-sheet-title">Advanced Option</span>
        <v-icon
          role="button"
          tabindex="0"
          aria-label="Close Advanced Options"
          class="advanced-sheet-close"
          @click="closeMobileSheet"
          @keydown.enter="closeMobileSheet"
          @keydown.space.prevent="closeMobileSheet"
          >mdi-close</v-icon
        >
      </div>

      <div class="advanced-sheet-body">
        <div class="advanced-sheet-field">
          <label class="advanced-sheet-label">ESTREAM Mindset</label>
          <SingleSelect
            v-model="mindsets"
            :options="mindsetOptions"
            placeholder="Select Mindset"
            :teleport="true"
            :clearable="true"
            :match-trigger-width="true"
            order="a-z"
          />
        </div>
        <div class="advanced-sheet-field">
          <label class="advanced-sheet-label">ESTREAM Skillset</label>
          <SingleSelect
            v-model="skillSets"
            :options="skillsetOptions"
            placeholder="Select Skillset"
            :teleport="true"
            :clearable="true"
            :match-trigger-width="true"
            order="a-z"
          />
        </div>
        <div class="advanced-sheet-field">
          <label class="advanced-sheet-label">Software</label>
          <SingleSelect
            v-model="software"
            :options="softwareOptions"
            placeholder="Select Software"
            :teleport="true"
            :match-trigger-width="true"
            :clearable="true"
            order="a-z"
          />
        </div>
        <div class="advanced-sheet-field">
          <label class="advanced-sheet-label">Student Profile</label>
          <MultipleSelect
            v-model="studentProfile"
            :options="profileOptions"
            placeholder="Select Student Profile"
            :teleport="true"
            :clearable="true"
            :match-trigger-width="true"
            order="a-z"
          />
        </div>
      </div>

      <v-divider class="advanced-sheet-divider"></v-divider>

      <div class="advanced-sheet-actions">
        <ActionButton
          variant="secondary"
          full-width
          @click="clearAdvancedFilters"
        >
          Clear Filter
        </ActionButton>
        <ActionButton
          variant="primary"
          full-width
          :disabled="!hasAnyFilter"
          @click="closeMobileSheet"
        >
          Apply Filter
        </ActionButton>
      </div>
    </div>
  </v-bottom-sheet>
</template>

<script setup>
import {
  computed,
  defineEmits,
  defineExpose,
  defineProps,
  inject,
  onMounted,
  ref,
  watch,
} from "vue";
import { useStore } from "vuex";
import { useDisplay } from "vuetify";
import LocalStorageService from "../services/localStorageService";
import TermService from "../services/termService";
import BaseInput from "./BaseInput.vue";
import ActionButton from "./common/ActionButton.vue";
import SingleSelect from "./common/SingleSelect.vue";
import MultipleSelect from "./common/MultipleSelect.vue";

const { mobile } = useDisplay();
const isMobile = computed(() => mobile.value);

// eslint-disable-next-line
const props = defineProps({
  disableButton: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();

const topic = ref("");
const grade = ref("");
const subject = ref("");
const mindsets = ref("");
const skillSets = ref("");
const techDomain = ref("");
const software = ref("");
const studentProfile = ref([]);
const showAdvancedOptions = ref(false);

const gradeOptions = ref([]);
const subjectOptions = ref([]);
const mindsetOptions = ref([]);
const skillsetOptions = ref([]);
const profileOptions = ref([]);
const softwareOptions = ref([]);

const isTopicError = ref(false);
const isGradeError = ref(false);
const isSubjectError = ref(false);

const topicErrorMessage = ref("");
const gradeErrorMessage = ref("");
const subjectErrorMessage = ref("");

const showDropdown = ref(false);

const colorButton = ref("#212121");

watch(
  [mindsets, skillSets, techDomain, software, studentProfile],
  ([mindsetVal, skillVal, techVal, softwareVal, profileVal]) => {
    colorButton.value =
      mindsetVal || skillVal || techVal || softwareVal || profileVal?.length > 0
        ? "#00925e"
        : "#212121";
  }
);

const mobileSheet = ref(false);

const toggleDropdown = () => {
  if (isMobile.value) {
    mobileSheet.value = !mobileSheet.value;
    showDropdown.value = mobileSheet.value;
  } else {
    showDropdown.value = !showDropdown.value;
  }
};

const closeMobileSheet = () => {
  mobileSheet.value = false;
  showDropdown.value = false;
};

const closeDesktopMenu = () => {
  showDropdown.value = false;
};

const clearAdvancedFilters = () => {
  mindsets.value = "";
  skillSets.value = "";
  software.value = "";
  studentProfile.value = [];
};

const hasAnyFilter = computed(
  () =>
    !!mindsets.value ||
    !!skillSets.value ||
    !!software.value ||
    (Array.isArray(studentProfile.value) && studentProfile.value.length > 0)
);

watch(mobileSheet, (val) => {
  if (!val) showDropdown.value = false;
});

const validateForm = () => {
  if (!topic.value) {
    isTopicError.value = true;
    topicErrorMessage.value = "Please insert your Topic!";
  } else {
    isTopicError.value = false;
    topicErrorMessage.value = "";
  }

  if (!grade.value) {
    isGradeError.value = true;
    gradeErrorMessage.value = "Choose your grade first";
  } else {
    isGradeError.value = false;
    gradeErrorMessage.value = "";
  }

  if (!subject.value) {
    isSubjectError.value = true;
    subjectErrorMessage.value = "Choose your subject first";
  } else {
    isSubjectError.value = false;
    subjectErrorMessage.value = "";
  }
};

const emit = defineEmits(["generate-clicked", "reset-form"]);

const emitGenerateClicked = () => {
  validateForm();
  if (!isTopicError.value && !isGradeError.value && !isSubjectError.value) {
    const data = {
      topic: topic.value,
      grade_term_id: grade.value,
      grades: gradeOptions.value.find((o) => o.value === grade.value)
        ? gradeOptions.value.find((o) => o.value === grade.value)?.text
        : "",
      subject_term_id: subject.value,
      subject: subjectOptions.value.find((o) => o.value === subject.value)
        ? subjectOptions.value.find((o) => o.value === subject.value)?.text
        : "",
      mindset_term_id: mindsets.value,
      mindsets: mindsetOptions.value.find((o) => o.value === mindsets.value)
        ? mindsetOptions.value.find((o) => o.value === mindsets.value)?.text
        : "",
      skillset_term_id: skillSets.value,
      skillsets: skillsetOptions.value.find((o) => o.value === skillSets.value)
        ? skillsetOptions.value.find((o) => o.value === skillSets.value)?.text
        : "",
      software_term_id: software.value,
      software: softwareOptions.value.find((o) => o.value === software.value)
        ? softwareOptions.value.find((o) => o.value === software.value)?.text
        : "",
      student: studentProfile.value,
    };
    const studentProfileTermName = Array.isArray(studentProfile.value)
      ? profileOptions.value
          .filter((o) => studentProfile.value.includes(o.value))
          .map((o) => o.text)
      : [];
    console.log("bank :", data);
    // Simpan data ke Vuex store
    // LocalStorageService.setFormData(data);
    store.commit("SET_FORM_DATA", {
      ...data,
      student_profile_term_name: studentProfileTermName,
    });
    emit("generate-clicked", data);
  }
};

const mappingFormData = () => {
  topic.value = store.state.formData.topic;
  grade.value = store.state.formData.grade_term_id;
  subject.value = store.state.formData.subject_term_id;
  mindsets.value = store.state.formData.mindset_term_id;
  skillSets.value = store.state.formData.skillset_term_id;
  software.value = store.state.formData.software_term_id;
  studentProfile.value = store.state.formData.student;
};

const mappingNullFormData = () => {
  const storedData = store.state.formData;
  const isEmpty = !storedData || Object.keys(storedData).length === 0;
  if (isEmpty) {
    const formData = LocalStorageService.getFormData();
    if (formData) {
      store.commit("SET_FORM_DATA", formData);
    }
  }
};

// eslint-disable-next-line
const resetForm = inject("resetForm", () => {
  topic.value = "";
  grade.value = "";
  subject.value = "";
  techDomain.value = "";
  software.value = "";
  studentProfile.value = [];
  showAdvancedOptions.value = false;
  isTopicError.value = false;
  isGradeError.value = false;
  isSubjectError.value = false;
  topicErrorMessage.value = "";
  gradeErrorMessage.value = "";
  subjectErrorMessage.value = "";
});

defineExpose({
  resetForm,
});

onMounted(async () => {
  // Prefill immediately before network calls
  mappingNullFormData();
  mappingFormData();

  try {
    const [
      gradesResponse,
      subjectsResponse,
      mindsetResponse,
      skillsetResponse,
      profileResponse,
    ] = await Promise.all([
      TermService.getFilters("grades"),
      TermService.getAllSubjects(),
      TermService.getFilters("mindsets"),
      TermService.getFilters("skillsets"),
      TermService.getFilters("student_profiles"),
    ]);

    gradeOptions.value = gradesResponse?.data?.data?.map((item) => ({
      text: item.name,
      value: item.slug,
    }));
    subjectOptions.value = subjectsResponse?.data?.data?.map((item) => ({
      text: item.name,
      value: item.slug,
    }));
    mindsetOptions.value = mindsetResponse?.data?.data?.map((item) => ({
      text: item.name,
      value: item.slug,
    }));
    skillsetOptions.value = skillsetResponse?.data?.data?.map((item) => ({
      text: item.name,
      value: item.slug,
    }));
    profileOptions.value = profileResponse?.data?.data?.map((item) => ({
      text: item.name,
      value: item.slug,
    }));

    mappingSubjects();
    mappingFormData();
  } catch (error) {
    console.error("Failed to fetch data:", error);
  }
});

const fetchSoftwares = async () => {
  try {
    const response = await TermService.getAllSoftwares(
      grade.value,
      mindsets.value,
      skillSets.value
    );
    softwareOptions.value = response?.data?.data?.map((item) => ({
      text: item.name,
      value: item.slug,
    }));
  } catch (error) {
    console.error("Error fetching software options:", error);
  }
};

// Watch for changes in each parameter and refetch when any of them changes
watch([grade, mindsets, skillSets], fetchSoftwares);

// Optionally, call fetch on mount if you want an initial fetch as well
onMounted(fetchSoftwares);

const mappingSubjects = () => {
  const sortedSubjects = subjectOptions.value.sort((a, b) => {
    if (a.text === "Other") return 1;
    if (b.text === "Other") return -1;
    return a.text.localeCompare(b.text);
  });

  subjectOptions.value = sortedSubjects;
};
</script>

<style scoped>
:root {
  --input-font-size: clamp(0.875rem, 0.8rem + 0.5vw, 1rem);
  --button-font-size: clamp(1rem, 0.9rem + 0.5vw, 1.2rem);
  --options-font-size: clamp(0.875rem, 0.8rem + 0.5vw, 1rem);
}

.prompt-title-row {
  display: flex;
  align-items: center;
  gap: 16px;
}

.prompt-title {
  font-size: var(--fs-xl);
  color: var(--primary-blue);
  font-family: "League Spartan";
  font-weight: 500;
  flex: 1;
  min-width: 0;
}

.filter-btn-wrapper {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 8px;
}

.mobile-toggle-btn {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
}

.mobile-toggle-btn :deep(.v-icon) {
  font-size: 24px;
}

.toggle-btn {
  border: 1px solid var(--button-color, #212121);
  border-radius: 8px;
  color: var(--button-color, #212121);
  width: 48px;
  height: 48px;
  min-width: unset;
}

.toggle-btn.has-filter,
.toggle-btn.active {
  background-color: #061f32;
  border-color: #003d6a;
  color: #ffffff;
}

.toggle-btn.has-filter :deep(.v-icon),
.toggle-btn.active :deep(.v-icon) {
  color: #ffffff;
}

.close-icon {
  border: 1px solid #212121;
  border-radius: 8px;
  color: #212121;
  width: 32px;
  height: 32px;
  min-width: unset;
}

.dropdown-title {
  color: var(--primary-blue);
  margin-bottom: 10px;
}

.dropdown-divider {
  width: 100%;
  margin: auto;
}

.dropdown-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 10px;
}

.form-row {
  margin-top: 0px;
}

.topic-col {
  display: flex;
}

.topic-input {
  resize: none;
  font-size: var(--input-font-size);
  display: flex;
  flex: 1;
  width: 100%;
}

.topic-input :deep(.input-wrap) {
  flex: 1;
  height: 100%;
}

.topic-input :deep(textarea.input-main) {
  flex: 1;
  height: 100%;
  min-height: 0;
}
.responsive-input {
  font-size: var(--input-font-size);
}

.responsive-input :deep(input.input-main),
.topic-input :deep(textarea.input-main) {
  border-radius: 8px;
  border: 1px solid var(--neutral-700);
  background: #fff;
}

.topic-input :deep(textarea.input-main) {
  resize: none;
}
.advanced-options {
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f2f3fb;
  border: 0.5px solid #d8dae5;
  border-radius: 10px;
  font-size: var(--options-font-size);
  font-family: "League Spartan", sans-serif;
  padding: 10px 20px;
  cursor: pointer;
  color: var(--primary-blue);
}

.advanced-options:hover {
  border-color: #cccccc;
  background-color: #eaecff;
}

.advanced-options-dropdown {
  position: absolute;
  right: 9%;
  margin-top: 10px;
  width: 30%;
  z-index: 2;
  padding: 1rem;
  border-radius: 16px;
  border: solid 1px #d8dae5;
  background-color: white;
  text-align: left;
}

.btn-generate {
  border-radius: 12px;
  font-size: var(--button-font-size);
  padding: 10px 20px;
}

.generate-button {
  width: 100%;
  font-size: var(--fs-big);
}

:deep(.input-label) {
  font-size: var(--fs-large);
  font-weight: 400;
}

.field-error {
  font-family: "League Spartan", sans-serif;
  font-size: var(--fs-small);
  color: var(--error-red, #c21d1d);
  margin-top: 4px;
}

.advanced-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.advanced-menu-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: 20px;
  line-height: 28px;
  color: var(--primary-blue, #003d6a);
}

.advanced-menu-close {
  color: #003d6a;
  cursor: pointer;
}

.advanced-menu-card {
  width: 392px;
  padding: 24px 16px;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background: #ffffff;
  overflow: visible;
}

.advanced-menu-card :deep(.single-select),
.advanced-menu-card :deep(.multiple-select) {
  position: static;
}

.advanced-menu-card .advanced-sheet-body {
  padding: 0;
}

.advanced-menu-card .advanced-sheet-actions {
  padding: 0;
}

.advanced-sheet {
  background: #ffffff;
  border-top-left-radius: 28px;
  border-top-right-radius: 28px;
  padding: 0 16px 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.advanced-sheet-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  border-bottom: 1px solid #d0d5dd;
}

.advanced-sheet-title {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: 22px;
  line-height: 28px;
  color: var(--primary-blue, #003d6a);
  flex: 1;
}

.advanced-sheet-close {
  color: #003d6a;
  cursor: pointer;
}

.advanced-sheet-body {
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 0 4px;
}

.advanced-sheet-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.advanced-sheet-label {
  font-family: "League Spartan", sans-serif;
  font-weight: 400;
  font-size: 20px;
  line-height: 28px;
  letter-spacing: 0.03px;
  color: #3b4648;
}

.advanced-sheet-divider {
  margin: 8px 0 0 0;
}

.advanced-sheet-actions {
  display: flex;
  gap: 8px;
  padding: 0 4px;
}

.advanced-sheet-actions :deep(.action-button) {
  flex: 1;
}

.advanced-sheet-field :deep(.single-select),
.advanced-sheet-field :deep(.select-trigger) {
  width: 100%;
}

@media (max-width: 768px) {
  :root {
    --input-font-size: clamp(0.813rem, 0.7rem + 0.5vw, 0.938rem);
    --button-font-size: clamp(0.875rem, 0.8rem + 0.5vw, 1.1rem);
    --options-font-size: clamp(0.813rem, 0.7rem + 0.5vw, 0.938rem);
  }

  .prompt-title {
    font-size: var(--fs-big);
  }

  .topic-input :deep(textarea.input-main) {
    height: 128px;
    min-height: 128px;
  }

  .advanced-options-dropdown {
    width: fit-content;
  }
}

@media (max-width: 480px) {
  :root {
    --input-font-size: clamp(0.75rem, 0.7rem + 0.5vw, 0.875rem);
    --button-font-size: clamp(0.813rem, 0.75rem + 0.5vw, 1rem);
    --options-font-size: clamp(0.75rem, 0.7rem + 0.5vw, 0.875rem);
  }

  .btn-generate {
    padding: 8px 16px;
  }

  .prompt-title {
    font-size: var(--fs-big);
    padding: 0 0.5rem;
  }
}
</style>
